import React from 'react'
import PropTypes from 'prop-types'

import FlatButton from 'material-ui/FlatButton'
import { connect } from 'react-redux'
import fileDownload from 'js-file-download'

import { NewInvoiceDialog, EditInvoiceDialog } from './InvoiceDialog'
import InvoicesList from './InvoicesList'
import BillingFilters from '../common/BillingFilters'
import ManualPaymentDialog from '../payments/ManualPaymentDialog'
import InvoicesStatusSelector from './InvoicesStatusSelector'
import SendBillDialog from '../common/SendBillDialog'
import SendBillStatusDialog from '../common/SendBillStatusDialog'
import DuplicateBillDialog from '../common/DuplicateBillDialog'
import ManualPaymentRecordStatusDialog from './ManualPaymentRecordStatusDialog'
import DownloadBillPDFStatusDialog from 'billing/common/DownloadBillPDFStatusDialog'
import CloudFunctionsApisUtils from 'infra/CloudFunctionsApisUtils'
import DbInvoiceEditor from '../infra/DbInvoiceEditor'
import Utils from '../infra/Utils'
import InvoicesRepository from '../infra/InvoicesRepository'

class Invoices extends React.Component {

  static propTypes = {
    teamId: PropTypes.string.isRequired,
    location: PropTypes.object
  }

  constructor(props) {
    super(props)
    this.state = {
      openMode: 'edit',
      newInvoice: false,
      openInvoice: false,
      openSendInvoiceDialog: false,
      showInvoiceSendStatusDialog: false,
      showInvoiceDuplicateDialog: false,
      showRecordPaymentDialog: false,
      showInvoiceRecordPaymentStatusDialog: false,
      showDownloadingPDFDialog: false,
      selectedTab: 'all',
      filters: {}
    }
  }

  componentDidMount() {
    const number = parseInt(this.props.location.query.number)
    if (number && !isNaN(number)) {
      InvoicesRepository.getInvoiceByNumber(this.props.teamId, number)
        .then(invoice => {
          if (invoice) {
            this.handleInvoiceViewRequested(invoice)
          }
        })
    }
  }

  openNewInvoiceDialog = event => {
    event.preventDefault()
    this.setState({
      newInvoice: true
    })
  }

  handleInvoiceViewRequested = selectedInvoice => {
    this.setState({
      openInvoice: true,
      openMode: 'preview',
      selectedInvoice
    })
  }

  handleInvoiceEditRequested = selectedInvoice => {
    this.setState({
      openInvoice: true,
      openMode: 'edit',
      selectedInvoice
    })
  }

  handleInvoiceDuplicateRequested = selectedInvoice => {
    this.setState({
      showInvoiceDuplicateDialog: true,
      invoiceDuplicateStatus: 'duplicating'
    })
    // Only copy invoice items and notes
    const copyOptions = { notes: true, items: true, photos: false, events: false }
    CloudFunctionsApisUtils.duplicateInvoice(this.props.teamId, selectedInvoice, copyOptions)
      .then(result => {
        this.setState({
          showInvoiceDuplicateDialog: false,
          openInvoice: true,
          openMode: 'edit',
          selectedInvoice: result.invoice
        })    
      })
      .catch(() => { this.setState({ invoiceDuplicateStatus: 'error' }) })
  }

  handleDuplicateInvoiceStatusDialogCloseRequested = () => {
    this.setState({
      showInvoiceDuplicateDialog: false
    })
  }

  handleInvoiceDialogClose = () => {
    this.setState({
      newInvoice: false,
      openInvoice: false
    })
  }

  handleStatusTabSelected = selectedTab => {
    this.setState({
      selectedTab,
      filters: Object.assign({}, this.state.filters, { status: selectedTab })
    })
  }

  handleFilterChanged = filters => {
    this.setState({ filters })
  }

  handleInvoiceSendSelected = invoice => {
    const invoiceEditor = new DbInvoiceEditor()
    invoiceEditor.initialize({
      invoice,
      teamId: this.props.teamId
    })
    this.setState({
      invoiceEditor,
      newInvoice: false,
      openInvoice: false,
      openSendInvoiceDialog: true
    })
  }

  handleSendInvoiceDialogCloseRequested = () => {
    this.setState({ openSendInvoiceDialog: false })
  }

  handleSendInvoiceClicked = emailInfo => {
    this.state.invoiceEditor.release()
    this.setState({
      openSendInvoiceDialog: false,
      showInvoiceSendStatusDialog: true,
      invoiceSendStatus: 'sending'
    })
    const invoiceId = this.state.invoiceEditor.getBillingEntity().id
    CloudFunctionsApisUtils.sendInvoiceEmail(this.props.teamId, invoiceId, emailInfo)
      .then(() => InvoicesRepository.updateInvoiceStatusOnEmailSent(this.props.teamId, invoiceId))
      .then(() => { this.setState({ invoiceSendStatus: 'success' }) })
      .catch(() => { this.setState({ invoiceSendStatus: 'error' }) })
  }

  handleSendInvoiceStatusDialogCloseRequested = () => {
    this.state.invoiceEditor.release()
    this.setState({
      showInvoiceSendStatusDialog: false
    })
  }

  handleInvoiceDownloadRequested = selectedInvoice => {
    this.setState({
      showDownloadingPDFDialog: true,
      selectedInvoice,
      downloadStatus: 'downloading'
    })
    CloudFunctionsApisUtils.downloadInvoicePdf(this.props.teamId, selectedInvoice.id)
      .then(({ content, headers }) => fileDownload(content,
        Utils.getFilenameFromHeadersOrDefault(headers, 'Invoice', selectedInvoice.number), 'application/pdf'))
      .then(() => { this.setState({ showDownloadingPDFDialog: false }) })
      .catch(() => { this.setState({ downloadStatus: 'error' }) })
  }

  handleDownloadInvoicePDFStatusDialogCloseRequested = () => {
    this.setState({ showDownloadingPDFDialog: false })
  }

  handleManualPaymentDialogCloseRequested = () => {
    this.setState({ showRecordPaymentDialog: false })
  }

  handleInvoiceRecordPaymentRequested = selectedInvoice => {
    this.setState({
      showRecordPaymentDialog: true,
      selectedInvoice
    })
  }

  handleSubmitPaymentClicked = paymentDetails => {
    this.setState({
      showRecordPaymentDialog: false,
      showInvoiceRecordPaymentStatusDialog: true,
      invoiceRecordPaymentStatus: 'recording'
    })
    CloudFunctionsApisUtils.recordInvoiceManualPayment(this.props.teamId, this.state.selectedInvoice, paymentDetails)
      .then(result => {
        this.setState({
          invoiceRecordPaymentStatus: 'success',
          amountRecorded: result.amountRecorded
        })
      })
      .catch(() => { this.setState({ invoiceRecordPaymentStatus: 'error' }) })
  }

  handleManualPaymentRecordStatusDialogCloseRequested = () => {
    this.setState({ showInvoiceRecordPaymentStatusDialog: false })
  }

  render() {
    return (
      <div style={styles.container}>
        <div style={styles.content}>
          <div style={styles.top}>
            <div style={styles.title}>Invoices</div>
            <FlatButton
              style={{border: '1px solid #e2e2e2'}}
              labelStyle={{ color: '#4d6f7f' }}
              label='Create new invoice'
              backgroundColor='#fff'
              onTouchTap={this.openNewInvoiceDialog}/>
          </div>
          <BillingFilters
            style={{ marginTop: 32 }}
            billingEntity='invoice'
            teamId={this.props.teamId}
            selectedTab={this.state.selectedTab}
            onFilterChanged={this.handleFilterChanged} />
          <InvoicesStatusSelector
            style={{ marginTop: 32 }}
            selectedTab={this.state.selectedTab}
            onStatusTabSelected={this.handleStatusTabSelected} />
          <InvoicesList
            style={{ marginTop: 32 }}
            filters={this.state.filters}
            teamId={this.props.teamId}
            onInvoiceSendSelected={this.handleInvoiceSendSelected}
            onInvoiceViewRequested={this.handleInvoiceViewRequested}
            onInvoiceEditRequested={this.handleInvoiceEditRequested}
            onInvoiceDuplicateRequested={this.handleInvoiceDuplicateRequested}
            onInvoiceDownloadRequested={this.handleInvoiceDownloadRequested}
            onInvoiceRecordPaymentRequested={this.handleInvoiceRecordPaymentRequested} />
        </div>
        <EditInvoiceDialog
          open={this.state.openInvoice}
          teamId={this.props.teamId}
          mode={this.state.openMode}
          invoice={this.state.selectedInvoice}
          onSendInvoiceSelected={this.handleInvoiceSendSelected}
          onCloseRequested={this.handleInvoiceDialogClose} />
        <NewInvoiceDialog
          open={this.state.newInvoice}
          teamId={this.props.teamId}
          mode={'edit'}
          onSendInvoiceSelected={this.handleInvoiceSendSelected}
          onCloseRequested={this.handleInvoiceDialogClose} />
        <SendBillDialog
          open={this.state.openSendInvoiceDialog}
          billEditor={this.state.invoiceEditor}
          billingEntity='invoice'
          onSendBillClicked={this.handleSendInvoiceClicked}
          onCloseRequested={this.handleSendInvoiceDialogCloseRequested} />
        <SendBillStatusDialog
          open={this.state.showInvoiceSendStatusDialog}
          billingEntity='invoice'
          status={this.state.invoiceSendStatus}
          onCloseRequested={this.handleSendInvoiceStatusDialogCloseRequested} />
        <DuplicateBillDialog
          open={this.state.showInvoiceDuplicateDialog}
          billingEntity='invoice'
          status={this.state.invoiceDuplicateStatus}
          onCloseRequested={this.handleDuplicateInvoiceStatusDialogCloseRequested} />
        <ManualPaymentDialog
          open={this.state.showRecordPaymentDialog}
          amountDue={Utils.getBillAmountDue(this.state.selectedInvoice)}
          onSubmitPaymentClicked={this.handleSubmitPaymentClicked}
          onCloseRequested={this.handleManualPaymentDialogCloseRequested} />
        <ManualPaymentRecordStatusDialog
          open={this.state.showInvoiceRecordPaymentStatusDialog}
          status={this.state.invoiceRecordPaymentStatus}
          amountRecorded={this.state.amountRecorded}
          onCloseRequested={this.handleManualPaymentRecordStatusDialogCloseRequested} />
        <DownloadBillPDFStatusDialog
          open={this.state.showDownloadingPDFDialog}
          status={this.state.downloadStatus}
          billingEntity='Invoice'
          onCloseRequested={this.handleDownloadInvoicePDFStatusDialogCloseRequested} />
      </div>
    )
  }

}

const styles = {
  container: {
    backgroundColor: '#fff',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflow: 'auto'
  },
  content: {
    marginLeft: 140,
    marginRight: 140,
    marginTop: 64,
    marginBottom: 64
  },
  top: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  title: {
    fontSize: 24,
    fontWeight: 400
  }
}

const mapStateToProps = state => ({ teamId: state.firebase.team.metadata.value.id })

export default connect(mapStateToProps)(Invoices)



// WEBPACK FOOTER //
// ./src/billing/invoices/Invoices.js